import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { CashReconciliationService } from '@app/employee/components/cash-reconciliation/cash-reconciliation.service';

import { OrdersService } from '@app/orders/services/orders.service';

import { CurrencyActionInputComponent } from '@shared/components/currency-action-input/currency-action-input.component';
import { StatusBadgeComponent } from '@app/employee/components/cash-reconciliation/components/status-badge/status-badge.component';
import { TitleBarComponent } from '@shared/components/title-bar/title-bar.component';
import { StatsCardComponent } from '@shared/components/stats-card/stats-card.component';
import { OrderStats } from '@app/orders/models/order.models';
import { DataModalComponent } from '@shared/components/wi-data-modal/wi-data-modal.component';
import { DateRangePickerComponent } from '@app/shared/components/wi-date-range-picker/wi-date-range-picker.component';
import { EmptyResultComponent } from '@shared/components/empty-result/empty-result.component';
import {
  mapReportToGrid,
  mapTransactionsResponse,
  formatCurrency,
} from '@app/employee/components/cash-reconciliation/utils/cash-reconciliation.mapper';

import {
  ReconciliationGridEmployee,
  AttendanceCashReconciliationResponse,
  CashReconciliationEntry,
  CashTransaction,
  CashReconciliationReportResponse,
  CashReconciliationReportRow,
} from '@app/employee/components/cash-reconciliation/interfaces/cash-reconciliation.interface';

import {
  statusIsPlain,
  getStatusConfig,
} from '@app/employee/components/cash-reconciliation/utils/cash-reconciliation.utils';

import { ReconciliationGridComponent } from '@app/employee/components/cash-reconciliation/components/reconciliation-grid/reconciliation-grid.component';

import {
  getShiftLabel,
  getStatusLabel,
  getStatusStyle,
  normalizeDeltaStatus,
} from '@app/employee/components/cash-reconciliation/helpers/reconciliation.helper';

import { ADMIN_ORDER_STATS } from '@app/orders/constants/orders.constants';
import { getActiveUserDataRaw } from '../../../shared/utils/impersonation-handoff.util';
import {
  disableFutureDates,
  getCashReconciliationToday,
} from '@app/employee/components/cash-reconciliation/helpers/date.helper';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
@Component({
  selector: 'app-staff-cash-reconciliation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TitleBarComponent,
    StatsCardComponent,
    CurrencyActionInputComponent,
    StatusBadgeComponent,
    DataModalComponent,
    DateRangePickerComponent,
    ReconciliationGridComponent,
    EmptyResultComponent,
    ClickOutsideDirective,
  ],
  templateUrl: './cash-reconciliation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cash-reconciliation.component.scss'],
})
export class StaffCashReconciliationComponent implements OnInit, OnDestroy {
  private static readonly RECONCILIATION_REFRESH_EVENTS = new Set([
    'admin-extra_cash_added',
    'admin-closing_balance_updated',
    'entry-not-closed',
    'admin-opening_balance_updated',
  ]);

  private readonly service = inject(CashReconciliationService);
  private readonly ordersService = inject(OrdersService);
  private readonly messageService = inject(NotificationService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private readonly cdr = inject(ChangeDetectorRef);

  selectedDate: Date = getCashReconciliationToday();
  calendarOpen = false;
  attendanceCashLoading = false;
  attendanceCash: AttendanceCashReconciliationResponse | null = null;
  closingBalanceDraft: number | null = null;
  closingSubmitted = false;
  closingValueChanged = false;
  submittingClosing = false;
  attendanceCalMonthCursor = getCashReconciliationToday();
  attendanceCalDays: Date[] = [];
  readonly attendanceWeekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  paymentModal: CashReconciliationEntry | null = null;
  paymentTx: CashTransaction[] = [];
  paymentModalOpen = false;
  paymentTxLoading = false;

  // Generate Reports (step 1: type, step 2: result)
  reportTypeModalOpen = false;
  selectedReportType: 'daily' | 'monthly' | 'custom' = 'daily';
  reportDailyDate: Date = getCashReconciliationToday();
  reportDate: Date = getCashReconciliationToday();
  selectedYear = getCashReconciliationToday().getFullYear();
  selectedMonth = getCashReconciliationToday().getMonth() + 1;

  /** Custom range (inline calendar) */
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  reportSumDeltas = 0;
  reportLoading = false;
  reportData: CashReconciliationReportRow[] = [];
  reportDateRangeLabel = '';
  reportLocationLabel = '';
  reportGeneratedAt: Date | null = null;
  reportTotalEntries = 0;
  reportTotalMatched = 0;
  reportTotalNotMatched = 0;
  calendarPopoverOpen = false;
  reportResultModalOpen = false;
  reportDateRangeModalOpen = false;

  fixStartDate: Date | null = null;
  fixEndDate: Date | null = null;

  reportGridData: ReconciliationGridEmployee[] = [];
  user: any | null = null;

  orderStats: OrderStats[] = ADMIN_ORDER_STATS.map(s => ({ ...s }));

  mismatchReasonModalOpen = false;
  mismatchReason = '';
  pendingClosingBalance: number | null = null;

  readonly getShiftLabel = getShiftLabel;
  readonly getStatusLabel = getStatusLabel;
  readonly getStatusStyle = getStatusStyle;
  readonly statusIsPlain = statusIsPlain;
  readonly getStatusConfig = getStatusConfig;
  readonly formatCurrency = formatCurrency;
  readonly disableFutureDates = disableFutureDates;

  ngOnInit(): void {
    try {
      this.user = JSON.parse(getActiveUserDataRaw() || '{}');
    } catch {
      this.user = {};
    }
    this.selectedDate = getCashReconciliationToday();
    this.attendanceCalMonthCursor = new Date(this.selectedDate);
    this.buildAttendanceCalDays();
    this.loadAttendanceCashByDate(this.selectedDate);
    this.loadOrderStats();

    this.notificationService.refresh$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (
            payload: { event?: string } | null | undefined
          ): payload is { event: string; data?: any } =>
            !!payload?.event &&
            StaffCashReconciliationComponent.RECONCILIATION_REFRESH_EVENTS.has(payload.event)
        )
      )
      .subscribe(() => {
        if (!this.router.url.startsWith('/cash-reconciliation')) {
          return;
        }
        this.loadAttendanceCashByDate(this.selectedDate);
        this.loadOrderStats();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get attendanceMonthLabel(): string {
    return this.attendanceCalMonthCursor.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  /*get shiftLabel(): ShiftOption {
    return this.attendanceCash?.shift === 'Evening' ? 'Evening' : 'Morning';
  }*/

  get canSubmitAttendanceClosing(): boolean {
    if (this.closingSubmitted || this.submittingClosing || !this.attendanceCash) {
      return false;
    }

    if (!this.closingValueChanged) {
      return false;
    }

    if (this.closingBalanceDraft === null || this.closingBalanceDraft === undefined) {
      return false;
    }

    return !Number.isNaN(this.closingBalanceDraft);
  }

  get isSubmittedClosingDisabled(): boolean {
    const submittedClosing = this.attendanceCash?.closingBalanceSubmitted;
    return submittedClosing != null && submittedClosing > 0;
  }

  formatAttendanceDetailTitle(): string {
    const d = this.stripDate(this.selectedDate);
    const short = d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const now = getCashReconciliationToday();
    return d.getTime() === now.getTime() ? `Today - ${short}` : short;
  }

  toggleCalendar(ev?: Event): void {
    ev?.stopPropagation();
    this.calendarOpen = !this.calendarOpen;
    if (this.calendarOpen) {
      this.attendanceCalMonthCursor = new Date(this.stripDate(this.selectedDate));
      this.buildAttendanceCalDays();
    }
  }

  onReconciliationDateSelect(date: Date | null): void {
    if (!date) return; // handle null safely

    const selected = this.stripDate(new Date(date));

    // prevent future dates
    if (selected.getTime() > getCashReconciliationToday().getTime()) {
      return;
    }

    this.selectedDate = selected;
    this.calendarOpen = false;
    this.loadAttendanceCashByDate(this.selectedDate);
  }

  loadAttendanceCashByDate(date: Date): void {
    const iso = this.formatDate(this.stripDate(new Date(date)));

    this.attendanceCashLoading = true;
    this.service
      .getAttendanceCash(iso)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.attendanceCash = data;

          /**
           * 0 is a valid closing balance.
           * Use match status instead of null check.
           */
          this.closingSubmitted =
            data.matchStatus === 'matched' || data.matchStatus === 'unmatched';

          this.closingBalanceDraft = data.closingBalanceSubmitted ?? null;

          this.closingValueChanged = false;

          this.attendanceCashLoading = false;
        },
        error: () => {
          this.attendanceCash = null;
          //this.syncCashStatsFromAttendance(null);
          this.attendanceCashLoading = false;
        },
      });
  }

  onClosingChange(v: number | null): void {
    this.closingBalanceDraft = v;

    const originalValue = this.attendanceCash?.closingBalanceSubmitted ?? null;

    this.closingValueChanged = v !== originalValue;
  }

  submitClosingBalance(): void {
    if (!this.canSubmitAttendanceClosing || this.closingBalanceDraft == null) {
      return;
    }

    if (!this.attendanceCash?.reconciliationId) {
      return;
    }

    const expected = this.attendanceCash?.expectedClosing ?? 0;
    const closing = Number(this.closingBalanceDraft);

    // mismatch check
    if (closing !== Number(expected)) {
      this.pendingClosingBalance = closing;
      this.mismatchReason = '';
      this.mismatchReasonModalOpen = true;
      return;
    }

    // normal matched submit
    this.updateClosingBalance(closing);
  }

  private updateClosingBalance(closingBalance: number, reason?: string): void {
    if (!this.attendanceCash?.reconciliationId) {
      return;
    }

    this.submittingClosing = true;

    this.service
      .updateEntry(this.attendanceCash.reconciliationId, {
        closing_balance: closingBalance,
        mismatch_reason: reason || null,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const expected = this.attendanceCash?.expectedClosing ?? 0;

          this.attendanceCash = {
            ...this.attendanceCash!,
            closingBalanceSubmitted: closingBalance,
            matchStatus: normalizeDeltaStatus(res.match_status) ?? null,
            delta: Number(closingBalance) - Number(expected),
          };

          this.closingSubmitted = true;
          this.closingValueChanged = false;
          this.closingBalanceDraft = closingBalance;
          this.submittingClosing = false;
          this.mismatchReasonModalOpen = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Closing balance updated!',
            detail: 'Closing balance has been updated successfully',
          });
        },

        error: err => {
          console.error('update closing balance failed', err);
          this.submittingClosing = false;
        },
      });
  }

  submitMismatchReason(): void {
    if (this.pendingClosingBalance == null || !this.mismatchReason.trim()) {
      return;
    }

    this.updateClosingBalance(this.pendingClosingBalance, this.mismatchReason.trim());
  }

  closeMismatchReasonModal(): void {
    this.mismatchReasonModalOpen = false;
    this.mismatchReason = '';
    this.pendingClosingBalance = null;
  }
  /*submitClosingBalance(): void {
    if (!this.canSubmitAttendanceClosing || this.closingBalanceDraft == null) {
      return;
    }
  
    if (!this.attendanceCash?.reconciliationId) {
      return;
    }
    this.submittingClosing = true;
  
    this.service
      .updateEntry(this.attendanceCash.reconciliationId, {
        closing_balance: this.closingBalanceDraft
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const closing = res.closing_balance ?? this.closingBalanceDraft;
          const expected = this.attendanceCash?.expectedClosing ?? 0;

          this.attendanceCash = {
            ...this.attendanceCash!,
            closingBalanceSubmitted: closing,
            matchStatus: normalizeDeltaStatus(res.match_status) ?? null,

            delta:
              closing != null
                ? Number(closing) - Number(expected)
                : null
          };
  
          this.closingSubmitted = true;
          this.closingValueChanged = false;
  
          this.closingBalanceDraft =
            res.closing_balance ?? this.closingBalanceDraft;
  
          //this.syncCashStatsFromAttendance(this.attendanceCash);
  
          this.submittingClosing = false;

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Closing balance updated!',
            detail:  `Closing balance has been updated successfully`
          });
        },
  
        error: (err) => {
          console.error('update closing balance failed', err);
          this.submittingClosing = false;
        }
      });
  }*/

  onViewPaymentDetails(): void {
    if (!this.attendanceCash) {
      return;
    }

    // Create single entry for logged-in user's attendance
    this.paymentModal = {
      id: this.attendanceCash.employeeId,
      shift: this.attendanceCash.shift,
      is_grayed: false,
      employee: {
        name: this.attendanceCash.employeeName,
      },
    } as CashReconciliationEntry;
    //this.paymentModal = this.attendanceCash
    this.paymentTx = [];
    this.paymentTxLoading = true;

    // open modal
    this.paymentModalOpen = true;

    const date = this.formatDate(this.selectedDate);

    this.service
      .getTransactions(this.attendanceCash?.employeeId || '', date)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: tx => {
          this.paymentTx = mapTransactionsResponse(tx);
          this.paymentTxLoading = false;
        },
        error: () => {
          this.paymentTxLoading = false;
        },
      });
  }

  closePaymentModal(): void {
    this.paymentModalOpen = false;
    this.paymentModal = null;
    this.paymentTx = [];
  }

  onViewReport(): void {
    console.log('view report', 'attendance-detail');
  }

  getFormattedDate(): string {
    return this.selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  paymentTotal(): number {
    return this.paymentTx.reduce((a, t) => a + t.amount, 0);
  }

  private toUtcYmd(d: Date): string {
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
      .toISOString()
      .slice(0, 10);
  }

  private stripDate(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private buildAttendanceCalDays(): void {
    const first = new Date(
      this.attendanceCalMonthCursor.getFullYear(),
      this.attendanceCalMonthCursor.getMonth(),
      1
    );
    const offset = first.getDay();
    const start = new Date(first);
    start.setDate(first.getDate() - offset);
    this.attendanceCalDays = Array.from({ length: 42 }, (_, i) => {
      const t = new Date(start);
      t.setDate(start.getDate() + i);
      return t;
    });
  }

  private initReportForm(): void {
    this.selectedReportType = 'daily';

    const d = getCashReconciliationToday();

    this.reportDate = d;
    this.reportDailyDate = d;

    this.selectedYear = d.getFullYear();
    this.selectedMonth = d.getMonth() + 1;

    this.selectedStartDate = null;
    this.selectedEndDate = null;
  }

  onGenerateReport(): void {
    this.initReportForm();
    this.reportTypeModalOpen = true;
  }

  formatReportCurrency(n: number | null): string {
    if (n == null) {
      return '—';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(n);
  }

  get canSubmitReportGenerate(): boolean {
    if (this.selectedReportType === 'daily') {
      return !!this.reportDate;
    }

    if (this.selectedReportType === 'monthly') {
      return !!this.selectedMonth && !!this.selectedYear;
    }

    if (this.selectedReportType === 'custom') {
      if (!this.fixStartDate || !this.fixEndDate) {
        return false;
      }

      const diff =
        Math.floor((this.fixEndDate.getTime() - this.fixStartDate.getTime()) / 86400000) + 1;

      return diff >= 1 && diff <= 30;
    }

    return false;
  }

  onReportGenerateClick(): void {
    if (!this.canSubmitReportGenerate) {
      return;
    }

    this.reportLoading = true;
    if (this.selectedReportType === 'daily') {
      const dateUtc = this.toUtcYmd(this.reportDate);
      this.service
        .getReport({ type: 'daily', dateUtc })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.applyReportResult(res, 'daily');
            this.cdr.markForCheck();
          },
          error: () => {
            this.reportLoading = false;
          },
        });
    } else if (this.selectedReportType === 'monthly') {
      const year = String(this.selectedYear);
      const month = String(this.selectedMonth).padStart(2, '0');
      this.service
        .getReport({ type: 'monthly', month, year })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.applyReportResult(res, 'monthly');
            this.cdr.markForCheck();
          },
          error: () => {
            this.reportLoading = false;
          },
        });
    } else {
      const startUtc = this.toUtcYmd(this.fixStartDate!);
      const endUtc = this.toUtcYmd(this.fixEndDate!);
      this.service
        .getReport({ type: 'custom', startUtc, endUtc })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.applyReportResult(res, 'custom');
            this.cdr.markForCheck();
          },
          error: () => {
            this.reportLoading = false;
          },
        });
    }
  }

  closeReportTypeModal(): void {
    this.reportTypeModalOpen = false;
  }

  closeReportResultModal(): void {
    this.reportResultModalOpen = false;
  }

  closeReportDateRangeModal(): void {
    this.reportDateRangeModalOpen = false;
  }

  cancelReportTypeModal(): void {
    this.closeReportTypeModal();
  }

  private applyReportResult(
    res: CashReconciliationReportResponse,
    type: 'daily' | 'monthly' | 'custom'
  ): void {
    this.reportData = res.rows;
    this.reportGridData = mapReportToGrid(res.rows);
    this.reportDateRangeLabel = res.date_range_label;
    this.reportLocationLabel = res.location;
    this.reportTotalEntries = res.total_entries;
    this.reportTotalMatched = res.total_matched;
    this.reportTotalNotMatched = res.total_not_matched;
    this.reportSumDeltas = res.sum_of_deltas;
    this.reportGeneratedAt = new Date();
    this.reportLoading = false;
    this.reportTypeModalOpen = false;

    if (type === 'daily') {
      this.reportResultModalOpen = true;
    } else {
      this.reportDateRangeModalOpen = true;
    }
  }

  twoDigit(n: number): string {
    if (n < 0) {
      return String(n);
    }
    return n.toString().padStart(2, '0');
  }

  trackByReportRowId(_: number, r: CashReconciliationReportRow): string {
    return r.id;
  }

  reportDeltaClass(
    delta: number | null
  ): 'cr-rep-delta--pos' | 'cr-rep-delta--neg' | 'cr-rep-delta--muted' {
    if (delta == null) {
      return 'cr-rep-delta--muted';
    }
    return delta < 0 ? 'cr-rep-delta--neg' : 'cr-rep-delta--pos';
  }

  downloadReportAsPdf(): void {
    let startDate = '';
    let endDate = '';

    // DAILY
    if (this.selectedReportType === 'daily') {
      const daily = this.toUtcYmd(this.reportDate);

      startDate = daily;
      endDate = daily;
    }

    // MONTHLY
    else if (this.selectedReportType === 'monthly') {
      const year = Number(this.selectedYear);
      const month = Number(this.selectedMonth);

      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);

      startDate = this.toUtcYmd(firstDay);
      endDate = this.toUtcYmd(lastDay);
    }

    // CUSTOM RANGE
    else {
      if (!this.fixStartDate || !this.fixEndDate) {
        return;
      }

      startDate = this.toUtcYmd(this.fixStartDate);
      endDate = this.toUtcYmd(this.fixEndDate);
    }

    this.reportLoading = true;

    this.service
      .downloadReportPdf({
        startDate,
        endDate,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.reportLoading = false;

          const blob = response.body;

          if (!blob) {
            return;
          }

          let fileName = 'cash-reconciliation-report.pdf';
          const contentDisposition = response.headers.get('content-disposition');

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);

            if (match?.[1]) {
              fileName = match[1];
            }
          }

          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;

          document.body.appendChild(a);
          a.click();

          document.body.removeChild(a);

          window.URL.revokeObjectURL(url);
        },

        error: err => {
          this.reportLoading = false;

          console.error('Failed to download cash reconciliation report PDF', err);
        },
      });
  }

  formatReportGeneratedAt(d: Date | null): string {
    if (!d) {
      return '—';
    }
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  onRangeChange(event: any) {
    this.fixStartDate = event.startDate;

    this.fixEndDate = event.endDate;
  }

  onMonthYearChange(event: any) {
    this.selectedMonth = event.month;

    this.selectedYear = event.year;
  }

  onDateChange(date: Date | null) {
    if (date) {
      this.reportDate = date;
    }
  }

  onReportRangeTypeChange(): void {
    // reusable component handles calendar switching
  }

  private loadOrderStats(): void {
    this.ordersService.getOrderStatistics().subscribe({
      next: stats => {
        this.orderStats = stats;
      },
      error: () => {
        // Keep default stats on error
      },
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
}
